import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CycleTimeDistribution } from '../hooks/useOverviewData';
import { formatDuration, formatNumber } from '../utils/format';
import { downloadCsv, todayYmd } from '../utils/csv';
import ExportButton from './ExportButton';

const DEFAULT_ROWS = 8;

interface Props {
    distributions: CycleTimeDistribution[] | null;
    loading: boolean;
}

/**
 * Per-stage cycle time distribution.
 *   - Solid bar = p50 (typical case)
 *   - Striped extension = p95 (worst-case tail)
 *   - Both scaled to the global max p95 so users see relative slowness across stages
 *
 * Sorted slowest-first (by p95 desc) so the diagnostic eye lands on
 * problem stages immediately.
 */
const CycleTimeCard: React.FC<Props> = ({ distributions, loading }) => {
    const intl = useIntl();
    const locale = intl.locale;
    const [expanded, setExpanded] = useState(false);

    const sorted = useMemo(() => {
        if (!distributions) return null;
        return [...distributions]
            .filter((d) => d.sample_size > 0 && d.p50 != null)
            .sort((a, b) => (b.p95 ?? 0) - (a.p95 ?? 0));
    }, [distributions]);

    const maxP95 = useMemo(
        () => Math.max(...(sorted?.map((d) => d.p95 ?? 0) ?? [0]), 1),
        [sorted],
    );

    const handleExport = () => {
        const payload = sorted ?? distributions ?? [];
        if (payload.length === 0) return;
        downloadCsv<CycleTimeDistribution>(
            `cycle-time-${todayYmd()}.csv`,
            [
                { header: 'Stage', accessor: (r) => r.name },
                { header: 'Sample size', accessor: (r) => r.sample_size },
                { header: 'P25 (seconds)', accessor: (r) => r.p25 ?? '' },
                { header: 'P50 (seconds)', accessor: (r) => r.p50 ?? '' },
                { header: 'P75 (seconds)', accessor: (r) => r.p75 ?? '' },
                { header: 'P95 (seconds)', accessor: (r) => r.p95 ?? '' },
                { header: 'Min (seconds)', accessor: (r) => r.min ?? '' },
                { header: 'Max (seconds)', accessor: (r) => r.max ?? '' },
            ],
            payload,
        );
    };

    return (
        <section className="av2-card">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage
                            id="analyticsV2.cycleTime.title"
                            defaultMessage="Time per stage"
                        />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage
                            id="analyticsV2.cycleTime.subtitle"
                            defaultMessage="How long products typically spend at each stage, slowest first"
                        />
                    </p>
                </div>
                <ExportButton
                    onClick={handleExport}
                    disabled={!sorted || sorted.length === 0}
                />
            </div>
            <div className="av2-card__body">
                {loading || !sorted ? (
                    <CycleTimeSkeleton count={5} />
                ) : sorted.length === 0 ? (
                    <div className="av2-empty">
                        <FormattedMessage
                            id="analyticsV2.cycleTime.empty"
                            defaultMessage="No completed stages in this period yet."
                        />
                    </div>
                ) : (
                    <ol className="av2-cycle">
                        {(expanded ? sorted : sorted.slice(0, DEFAULT_ROWS)).map((d, idx) => {
                            const p50Pct = d.p50 != null ? (d.p50 / maxP95) * 100 : 0;
                            const p95Pct = d.p95 != null ? (d.p95 / maxP95) * 100 : 0;
                            const tailPct = Math.max(0, p95Pct - p50Pct);
                            return (
                                <li
                                    key={d.responsibility_id}
                                    className="av2-cycle__row"
                                    style={{ animationDelay: `${idx * 40}ms` }}
                                >
                                    <div className="av2-cycle__header">
                                        <span className="av2-cycle__name">{d.name}</span>
                                        <span className="av2-cycle__metrics">
                                            <span className="av2-cycle__typical">
                                                <FormattedMessage
                                                    id="analyticsV2.cycleTime.typical"
                                                    defaultMessage="typical"
                                                />{' '}
                                                <strong>{formatDuration(d.p50)}</strong>
                                            </span>
                                            <span className="av2-cycle__slowest">
                                                <FormattedMessage
                                                    id="analyticsV2.cycleTime.slowest"
                                                    defaultMessage="slowest 5%"
                                                />{' '}
                                                <strong>{formatDuration(d.p95)}</strong>
                                            </span>
                                        </span>
                                    </div>
                                    <div className="av2-cycle__bar">
                                        <span className="av2-cycle__p50" style={{ width: `${p50Pct}%` }} />
                                        <span className="av2-cycle__p95" style={{ width: `${tailPct}%` }} />
                                    </div>
                                    <div className="av2-cycle__meta">
                                        <FormattedMessage
                                            id="analyticsV2.cycleTime.sampleSize"
                                            defaultMessage="{count, plural, one {# completion} other {# completions}}"
                                            values={{ count: d.sample_size }}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                        {sorted.length > DEFAULT_ROWS && (
                            <button
                                type="button"
                                className={`av2-show-all${expanded ? ' av2-show-all--expanded' : ''}`}
                                onClick={() => setExpanded((v) => !v)}
                            >
                                {expanded ? (
                                    <FormattedMessage
                                        id="analyticsV2.showLess"
                                        defaultMessage="Show less"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="analyticsV2.showAll"
                                        defaultMessage="Show all ({count})"
                                        values={{ count: sorted.length }}
                                    />
                                )}
                                <span className="av2-show-all__chevron" aria-hidden="true">
                                    ⌄
                                </span>
                            </button>
                        )}
                    </ol>
                )}
            </div>
        </section>
    );
};

const CycleTimeSkeleton: React.FC<{ count: number }> = ({ count }) => {
    return (
        <ol className="av2-cycle">
            {Array.from({ length: count }, (_, i) => (
                <li key={i} className="av2-cycle__row av2-cycle__row--skeleton">
                    <div className="av2-cycle__header">
                        <div className="av2-skel av2-skel--cycle-name" />
                        <div className="av2-skel av2-skel--cycle-meta" />
                    </div>
                    <div className="av2-skel av2-skel--cycle-bar" />
                </li>
            ))}
        </ol>
    );
};

export default CycleTimeCard;
